import React from 'react';
import { CalendarDaysIcon, TrashIcon } from '@heroicons/react/24/outline';
import { IoMdBarcode } from 'react-icons/io';
import Barcode from 'react-barcode';
import { Modal } from '@/components/elements/Modal';
import {
  ExpandableCard,
  ExpandablePanel,
} from '@/components/elements/ExpandablePanel';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { getDateTime } from '@/utils/dateTime';
import { Text, Caption } from '@/components';
import { PRODUCTS_PROPS } from '@/constant/product/product.const';
import { APP_BORDER } from '@/constant/theme/colors.constant';

// ─── Props ────────────────────────────────────────────────────────────────────
interface ProductBarcodeDetailsDialogProps {
  barcodeDetails: any;
  openProductBarcodeDialog: boolean;
  handleProductBarcodeDialogOpen: () => void;
}

// ─── Dialog ───────────────────────────────────────────────────────────────────
const ProductBarcodeDetailsDialog: React.FC<
  ProductBarcodeDetailsDialogProps
> = ({
  barcodeDetails,
  openProductBarcodeDialog,
  handleProductBarcodeDialogOpen,
}) => {
  const barcodes: any[] = barcodeDetails ?? [];
  const activeCount = barcodes.filter((b) => !b.deletedOn).length;
  const deletedCount = barcodes.filter((b) => b.deletedOn).length;

  const toolbar = (
    <div
      className={`flex items-center gap-2 px-4 pt-3 pb-2 border-b ${APP_BORDER.soft}`}
    >
      <PillCell
        value={`${activeCount} Active`}
        preset="boolEnabled"
        dot
        size="xs"
      />
      {deletedCount > 0 && (
        <PillCell
          value={`${deletedCount} Deactivated`}
          preset="inactive"
          dot
          size="xs"
        />
      )}
    </div>
  );

  return (
    <Modal
      open={openProductBarcodeDialog}
      onClose={handleProductBarcodeDialogOpen}
      size="2xl"
    >
      <Modal.Header
        title={PRODUCTS_PROPS.DIALOGS.BARCODE_TITLE}
        subtitle={`${barcodes.length} barcode${barcodes.length !== 1 ? 's' : ''} · ${activeCount} active · ${deletedCount} deactivated`}
        icon={<IoMdBarcode className="w-4 h-4" />}
        onClose={handleProductBarcodeDialogOpen}
      />
      <Modal.Body noPadding>
        <ExpandablePanel
          items={barcodes}
          getItemId={(b) => String(b.code)}
          multiExpand
          toolbar={toolbar}
          emptyIcon={<IoMdBarcode />}
          emptyTitle={PRODUCTS_PROPS.DIALOGS.NO_BARCODES_TITLE}
          emptyDescription={PRODUCTS_PROPS.DIALOGS.NO_BARCODES_DESC}
          renderItem={(b, expanded, onToggle) => {
            const isDeleted = Boolean(b?.deletedOn);
            return (
              <ExpandableCard
                key={b.code}
                expanded={expanded}
                onToggle={onToggle}
                accentColor={isDeleted ? '#ef4444' : '#10b981'}
                showChevron
                header={
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                        isDeleted
                          ? 'bg-gradient-to-br from-red-400 to-red-600'
                          : 'bg-gradient-to-br from-emerald-400 to-emerald-600'
                      }`}
                    >
                      <span className="text-white [&>*]:w-4 [&>*]:h-4">
                        <IoMdBarcode />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text
                        as="span"
                        size="base"
                        weight="semibold"
                        color="soft"
                        className="font-mono"
                      >
                        {b.code}
                      </Text>
                    </div>
                    <div className="flex-shrink-0 hidden sm:flex flex-col items-end text-right">
                      <Caption size="sm" color="subtle">
                        {isDeleted ? 'Deactivated' : 'Last Updated'}
                      </Caption>
                      <Text
                        as="p"
                        size="md"
                        weight="semibold"
                        color={isDeleted ? 'danger' : 'soft'}
                        className="flex items-center gap-1 mt-0.5"
                      >
                        {isDeleted ? (
                          <>
                            <TrashIcon className="w-3 h-3" />
                            {getDateTime(b.deletedOn)}
                          </>
                        ) : (
                          <>
                            <CalendarDaysIcon className="w-3 h-3 text-primary" />
                            {getDateTime(b.updatedOn)}
                          </>
                        )}
                      </Text>
                    </div>
                    <PillCell
                      value={isDeleted ? 'Deactivated' : 'Active'}
                      preset="boolEnabled"
                      dot
                      size="xs"
                    />
                  </div>
                }
              >
                <div className="flex justify-center py-4">
                  <Barcode height={42} value={b.code} displayValue />
                </div>
                {b.createdOn && (
                  <div
                    className={`border-t ${APP_BORDER.soft} px-4 py-2 flex items-center gap-1`}
                  >
                    <CalendarDaysIcon className="w-3 h-3" />
                    <Text as="span" size="md" color="muted">
                      Created: {getDateTime(b.createdOn)}
                    </Text>
                  </div>
                )}
              </ExpandableCard>
            );
          }}
        />
      </Modal.Body>
    </Modal>
  );
};

export default ProductBarcodeDetailsDialog;
